import React from 'react'
import { Link } from 'react-router'
import { useCreate, useUpdate } from '@refinedev/core'
import { App, Button, Segmented, Switch, Tooltip } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, type Stat } from '../../theme/listkit'
import { Store, Target, ListFilter } from '../../components/icons'
import {
  useJaguarOwnedSwitches,
  JAGUAR_OWNED_FLAG_KEYS,
  isJaguarOwnedOwned,
  CONTROLS_CATALOG,
  type ControlCatalogEntry,
  type FeatureFlagRow,
} from './useJaguarOwnedSwitches'

// Re-exported so call sites that already import the page (feature_flags/list.tsx,
// mirroring isOnboardingOwned / isDemoOwned) get everything from one place.
export { JAGUAR_OWNED_FLAG_KEYS, isJaguarOwnedOwned, CONTROLS_CATALOG }
// ADMINS_ONLY_DESCRIPTION is exported separately, below its own declaration
// (it isn't part of CONTROLS_CATALOG — see the comment there) — tests pin
// it so a future copy edit here and in a test can't silently drift apart.

const PART_KEYS = [
  'jaguar_owned_spotlight_enabled',
  'jaguar_owned_perks_enabled',
  'jaguar_owned_ask_enabled',
  'jaguar_owned_vouches_enabled',
  'jaguar_owned_notes_enabled',
]

/**
 * Task S3: the hub row's three states, backed by the two rows described in
 * `semantics.md` (`jaguar_owned_enabled`, `jaguar_owned_admins_only`) — see
 * the big comment on `JaguarOwnedControlsPage` below for the write-ordering
 * rule that keeps a two-row state change from ever showing a wider audience
 * than intended, even for an instant.
 */
type HubAudience = 'off' | 'admins' | 'everyone'

/** Widest last — used to tell narrowing from widening (`RANK[next] < RANK[current]`). */
const HUB_AUDIENCE_RANK: Record<HubAudience, number> = { off: 0, admins: 1, everyone: 2 }

const HUB_AUDIENCE_OPTIONS: { label: string; value: HubAudience }[] = [
  { label: 'Off', value: 'off' },
  { label: 'Admins only', value: 'admins' },
  { label: 'Everyone', value: 'everyone' },
]

const HUB_AUDIENCE_LABEL: Record<HubAudience, string> = {
  off: 'Off',
  admins: 'Admins only',
  everyone: 'Everyone',
}

/** `jaguar_owned_admins_only` has no `CONTROLS_CATALOG` entry of its own (it's
 * not a switch), so it needs its own `description` for the create path. */
export const ADMINS_ONLY_DESCRIPTION =
  'Restricts the Jaguar-Owned hub to admins while it is on — see the hub row on this page.'

function errorMessage(e: unknown): string {
  return (e as { message?: string } | undefined)?.message ?? 'unknown error'
}

/**
 * Postgres's unique-violation code. The Supabase data provider's `handleError`
 * spreads the original `PostgrestError` onto the thrown object before setting
 * `message`/`statusCode` (`@refinedev/supabase`'s `handleError`), so `code`
 * survives untouched as the string `'23505'` — the same shape `SpotlightForm.tsx`'s
 * `saveErrorMessage` reads. The `cause` branch is defensive only: nothing in
 * this codebase's mutation path wraps the error that way today, but a thrown
 * value could still carry one.
 */
function errorCode(e: unknown): string | undefined {
  const err = e as { code?: unknown; cause?: { code?: unknown } } | null | undefined
  if (!err || typeof err !== 'object') return undefined
  if (typeof err.code === 'string') return err.code
  if (err.cause && typeof err.cause === 'object' && typeof err.cause.code === 'string') return err.cause.code
  return undefined
}

/**
 * Jaguar-Owned controls — the curated page that owns all eight
 * `jaguar_owned_*` flags (Task C4, plus Task C5's vouches and notes rows,
 * plus Task S1's `jaguar_owned_admins_only`). Unlike the generic Feature
 * Flags list, every catalog entry always shows here, even before its row
 * exists (a missing row already reads as "off" everywhere else) — toggling
 * ON a not-yet-created flag inserts it.
 *
 * Six of the seven catalog rows are plain switches. Turning one ON writes
 * immediately. Turning one OFF confirms first (`App.useApp().modal.confirm`
 * — never the static `Modal`, which doesn't mount under this app's React 19
 * setup) with exactly what the brief says disappears; cancelling leaves the
 * switch and the data untouched.
 *
 * The seventh — the hub row, `jaguar_owned_enabled` — is not a switch.
 * Task S3 gives it three states (Off · Admins only · Everyone) rendered as
 * an AntD `Segmented` (`data-testid="jo-control-hub-audience"`), chosen over
 * `Radio.Group` because it reads as one control with a moving selection
 * rather than three independent radios, which matches "the hub is in
 * exactly one of three states" better than a radio group's "each of these
 * is independently checked or not" affordance — and it's the same compact
 * three-way shape already familiar from other AntD 5 admin surfaces.
 *
 * **The state is backed by TWO rows** (`jaguar_owned_enabled` and
 * `jaguar_owned_admins_only` — `semantics.md` in this task's SDD folder has
 * the server-side truth table), so selecting a state is a two-write
 * operation: `applyHubAudienceChange` below. Two things make that
 * two-write operation atomic from the ADMIN'S point of view, even though
 * there is no database transaction wrapping the two REST calls:
 *
 *   1. **No half-applied state is ever visible in the UI.** Exactly like
 *      the switches, the Segmented's selected value is computed straight
 *      from `byKey` — never a local optimistic guess — and the control is
 *      disabled (`pending['jaguar_owned_enabled']`, the same pending map
 *      the switches use, keyed by the hub's own row key) from the moment
 *      the change starts until BOTH writes have settled and a follow-up
 *      `refetch()` has landed. The admin cannot see, or interact with, a
 *      moment where one row has changed and the other hasn't. For a
 *      NARROWING move, "the moment the change starts" is fix round M2's own
 *      fix: `handleHubAudienceChange` claims the lock the instant it opens
 *      the confirm dialog, not the instant `onOk` fires — see that
 *      function's own comment for the leak this closes (two dialogs open
 *      at once, the second confirm silently dropped).
 *
 *   2. **The two writes are ordered so the real, member-facing state is
 *      never WIDER than either the state it started in or the state it's
 *      headed to.** `jaguar_owned_admins_only` is the row that decides who
 *      is let in WHILE the hub is on; `jaguar_owned_enabled` is the row
 *      that turns visibility on at all. So:
 *        - Target **Off**: write `enabled: false` FIRST. That alone hides
 *          the hub from everyone (`admins_only` doesn't matter once
 *          `enabled` is false — see `semantics.md`'s table), so the
 *          member-facing state reaches "nobody sees it" on the very first
 *          write, not after both have landed.
 *        - Target **Admins only** or **Everyone**: write `admins_only` to
 *          its target value FIRST, then `enabled: true` second. Whichever
 *          audience is intended is already in place by the time visibility
 *          flips on, so the hub is never visible-but-unscoped even for an
 *          instant — there's no window where a hub meant to open to admins
 *          only is briefly open to everyone (or vice versa).
 *      A consequence worth naming: because the FIRST write of the pair is
 *      the one that actually flips the real member-facing behaviour, a
 *      failure of the SECOND write (a reaffirming, often no-op value) never
 *      leaves the server in a state that's wider than intended — at worst
 *      it leaves the OTHER row exactly where it already was, which
 *      `semantics.md`'s table confirms is always a safe (never oversharing)
 *      combination. Every write still goes through the same `refetch()`
 *      and error-toast handling as a failed switch write, so the control
 *      never trusts an assumption about what the second write did — it
 *      shows whatever the server refetch actually says.
 *
 * Confirm-vs-immediate follows the same "never confirm a widening" rule as
 * the rest of this page, decided purely by comparing `HUB_AUDIENCE_RANK`
 * (Off < Admins only < Everyone): picking a LOWER rank than the current
 * selection is a narrowing and confirms first (`Everyone → Admins only`
 * gets its own title/content; anything `→ Off` reuses the hub's existing
 * `offImpact` copy, same as a switch's turn-off confirm); picking an EQUAL
 * or HIGHER rank applies immediately, with no dialog.
 *
 * There is no local "optimistic" copy of a row's value: the switch renders
 * `row.enabled` straight from `byKey` (the fetched data), and a row's own
 * `pending` flag just disables its switch while a write is outstanding.
 *
 * A failed write refetches too (fix round 3, M2) — success and failure both
 * end with the row re-synced to whatever the server actually holds, wrapped
 * in its own try/catch so a refetch hiccup right after a genuine failure
 * doesn't hide the original error toast. Without this, a stale fetched row
 * (for example the admin's tab was open when `db push` seeded a switch, or
 * another admin flipped it) could show NOT SET/off forever after the first
 * failed click, since nothing else ever re-pulled the real value. A `create`
 * that fails with Postgres `23505` (`errorCode` above) — the row appeared
 * between this page's fetch and the click — retries once as the `update` it
 * should have been, with the same values, before falling through to the
 * error toast.
 *
 * Every write goes through `mutateAsync` (fix round 2, item 1) — NOT the
 * `mutate` + `{ onSuccess, onError }` callback form. All eight rows —
 * including the hub audience control's TWO, one after the other — share ONE
 * `useUpdate()`/`useCreate()` hook call, which means ONE shared
 * `MutationObserver` underneath. Calling `.mutate()` again for row B while
 * row A's write is still in flight makes that shared observer drop row A's
 * observer registration (`MutationObserver.mutate()` does
 * `this.#currentMutation?.removeObserver(this)` before building the new
 * one) — so row A's `onSuccess`/`onError` never fire, `pending` for row A
 * never clears, and its switch stays disabled until the page reloads.
 * `mutateAsync()`'s returned promise is tied to that SPECIFIC mutation's own
 * execution, not to the observer's "current mutation" bookkeeping, so
 * awaiting it per call sidesteps the bug entirely — each row's write
 * settles (and un-disables that row) independently of what any other row is
 * doing at the same time. This is exactly why the hub audience control's two
 * writes are `await`ed one after another (never `Promise.all`'d) inside
 * `applyHubAudienceChange`: sequential `mutateAsync` calls on the same
 * shared observer are exactly what this hook call was already proven safe
 * for, and sequencing them is also what makes the write-ordering guarantee
 * above possible in the first place — a concurrent pair couldn't promise
 * which row lands first.
 *
 * `pendingRef` (a plain ref, not state) is the double-submit guard: it is
 * checked and set SYNCHRONOUSLY at the top of `applyChange` (and, for the
 * hub row, `applyHubAudienceChange`), so a second click that lands before
 * React has committed the `pending` state update (and before the control's
 * own `disabled` prop takes effect) still can't fire a second mutation for
 * the same key — this is what keeps a missing row's "turn on" from calling
 * `create` twice, and what keeps a second click on the Segmented from
 * starting a second two-row write while the first is still in flight.
 */
export function JaguarOwnedControlsPage() {
  // `retry: false` (M3) — this page has its own manual "Retry" button, so a
  // silent background retry would fight it. The four other readers of this
  // hook omit the option and get the app-wide default instead.
  const { byKey, isLoading, isFetching, isError, refetch } = useJaguarOwnedSwitches({ retry: false })
  const { mutateAsync: updateAsync } = useUpdate()
  const { mutateAsync: createAsync } = useCreate()
  const { modal, message } = App.useApp()

  const pendingRef = React.useRef<Set<string>>(new Set())
  const [pending, setPending] = React.useState<Record<string, boolean>>({})

  // A first-ever load with no cached data yet reports `isLoading: true` and
  // `isError: false` for the ENTIRE duration of a refetch too — including a
  // refetch kicked off by clicking Retry after a failure. Without tracking
  // that explicitly, the page would swap the error block (and its Retry
  // button) out for the plain "Loading…" placeholder the instant Retry is
  // clicked, which is exactly backwards for item 5: the button that should
  // show its OWN spinner would instead disappear. `retrying` keeps the
  // error block (and the button) mounted for exactly that one window.
  const [retrying, setRetrying] = React.useState(false)
  async function handleRetry() {
    setRetrying(true)
    try {
      await refetch()
    } finally {
      setRetrying(false)
    }
  }

  function setKeyPending(key: string, value: boolean) {
    if (value) pendingRef.current.add(key)
    else pendingRef.current.delete(key)
    setPending((prev) => ({ ...prev, [key]: value }))
  }

  // Shared by `applyChange` (the six plain switches) and
  // `applyHubAudienceChange` (the hub row's two-row write) — the create/
  // update-with-23505-retry logic used to live only inside `applyChange`;
  // pulling it out means the hub audience control reuses the EXACT same
  // retry contract for each of its two rows, rather than a second,
  // possibly-drifting copy of it.
  async function writeFlagRow(key: string, row: FeatureFlagRow | null, next: boolean, description: string) {
    const mutationOpts = { successNotification: false as const, errorNotification: false as const }
    if (row) {
      await updateAsync({
        resource: 'feature_flags',
        id: key,
        values: { enabled: next },
        meta: { idColumnName: 'key' },
        ...mutationOpts,
      })
      return
    }
    // First touch — create the row (a missing row already reads as "off").
    try {
      await createAsync({
        resource: 'feature_flags',
        values: { key, enabled: next, description },
        meta: { idColumnName: 'key' },
        ...mutationOpts,
      })
    } catch (createErr) {
      // 23505 = the row appeared meanwhile (another admin's tab, or a
      // migration seeding it after this page's own fetch ran but before
      // the click). The row exists now, so finish the job the way we
      // would have if we'd fetched a moment later: retry once as the
      // update this should have been, with the same values. Anything
      // else re-throws to the caller.
      if (errorCode(createErr) !== '23505') throw createErr
      await updateAsync({
        resource: 'feature_flags',
        id: key,
        values: { enabled: next },
        meta: { idColumnName: 'key' },
        ...mutationOpts,
      })
    }
  }

  async function applyChange(entry: ControlCatalogEntry, row: FeatureFlagRow | null, next: boolean) {
    if (pendingRef.current.has(entry.key)) return // a write for this key is already in flight
    setKeyPending(entry.key, true)
    try {
      await writeFlagRow(entry.key, row, next, entry.note)
      try {
        await refetch()
      } catch {
        // The write itself already succeeded — a refetch hiccup right after
        // it shouldn't turn into an error toast for a change that landed.
        // The next focus refetch (or the "Retry"/reload the error state
        // offers) picks up the correct value.
      }
      message.success(`${entry.title} turned ${next ? 'on' : 'off'}.`)
    } catch (e) {
      message.error(`Couldn't change ${entry.title}: ${errorMessage(e)}`)
      try {
        await refetch()
      } catch {
        // Best effort: the write failed, so there's no new value to sync to
        // anyway. If the refetch also fails the switch just keeps showing
        // its last known-good value, same as any other load failure.
      }
    } finally {
      setKeyPending(entry.key, false)
    }
  }

  function handleToggle(entry: ControlCatalogEntry, row: FeatureFlagRow | null, checked: boolean) {
    if (pendingRef.current.has(entry.key)) return
    if (checked) {
      void applyChange(entry, row, true)
      return
    }
    modal.confirm({
      title: `Turn off ${entry.title}?`,
      content: `${entry.offImpact} Nothing is deleted, and you can turn it back on at any time.`,
      okText: 'Turn off',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => applyChange(entry, row, false),
    })
  }

  const hubEntry = CONTROLS_CATALOG.find((c) => c.key === 'jaguar_owned_enabled')!
  const hubRow = byKey.get('jaguar_owned_enabled') ?? null
  const adminsOnlyRow = byKey.get('jaguar_owned_admins_only') ?? null
  // A missing `jaguar_owned_enabled` row reads as off, same as every other
  // row on this page. A missing `jaguar_owned_admins_only` row reads as
  // "not in preview" — open to everyone — the exact default `semantics.md`
  // documents for a database that hasn't (yet, or ever) written that row.
  const hubOn = hubRow?.enabled ?? false
  const hubAdminsOnly = adminsOnlyRow?.enabled ?? false
  const hubAudience: HubAudience = !hubOn ? 'off' : hubAdminsOnly ? 'admins' : 'everyone'

  // The hub row's own pending flag is keyed by `jaguar_owned_enabled` — the
  // same key `pending`/`pendingRef` already use for it (no plain Switch
  // reads that key any more, so reusing it here is unambiguous), which is
  // enough to disable the Segmented as a single unit across BOTH of its
  // writes.
  //
  // Fix round (M2): this function no longer claims `pendingRef` itself — its
  // ONLY caller, `handleHubAudienceChange` below, claims it synchronously
  // the moment a change is accepted, BEFORE a confirm dialog (if any) even
  // opens. Re-claiming (and re-checking) it here too used to be exactly the
  // gap M2 named: the lock wasn't set until `onOk` actually fired, so two
  // narrowing dialogs (e.g. Off then Admins only, opened back to back while
  // the first is still showing) could both be open at once, and confirming
  // the second while the first's writes were in flight hit this function's
  // own `if (pendingRef.current.has(key)) return` and silently no-opped —
  // dialog closed, no toast, no error, but nothing written. Claiming at
  // dialog-open time instead means the SECOND click never gets a dialog at
  // all: `handleHubAudienceChange`'s own guard (identical shape to this
  // function's old one) now sees the lock already held.
  async function applyHubAudienceChange(next: HubAudience) {
    const key = 'jaguar_owned_enabled'
    try {
      if (next === 'off') {
        // Cut visibility FIRST — `enabled: false` alone already hides the
        // hub from everyone regardless of `admins_only` (see the class
        // comment above), so the real, member-facing state reaches "nobody
        // sees it" on this write, not the one after it.
        await writeFlagRow('jaguar_owned_enabled', hubRow, false, hubEntry.note)
        await writeFlagRow('jaguar_owned_admins_only', adminsOnlyRow, true, ADMINS_ONLY_DESCRIPTION)
      } else {
        // Set the audience gate BEFORE turning visibility on, so the
        // instant the hub becomes visible it is already scoped to the
        // intended audience.
        await writeFlagRow('jaguar_owned_admins_only', adminsOnlyRow, next === 'admins', ADMINS_ONLY_DESCRIPTION)
        await writeFlagRow('jaguar_owned_enabled', hubRow, true, hubEntry.note)
      }
      try {
        await refetch()
      } catch {
        // Both writes already succeeded — see the matching comment in
        // `applyChange` for why a refetch hiccup here isn't an error toast.
      }
      message.success(`Jaguar-Owned hub set to ${HUB_AUDIENCE_LABEL[next]}.`)
    } catch (e) {
      message.error(`Couldn't change the Jaguar-Owned hub: ${errorMessage(e)}`)
      try {
        await refetch()
      } catch {
        // Best effort, same rationale as `applyChange`'s failure path.
      }
    } finally {
      setKeyPending(key, false)
    }
  }

  function handleHubAudienceChange(next: HubAudience) {
    const key = 'jaguar_owned_enabled'
    if (pendingRef.current.has(key)) return
    if (next === hubAudience) return // clicking the already-selected segment
    const narrowing = HUB_AUDIENCE_RANK[next] < HUB_AUDIENCE_RANK[hubAudience]
    if (!narrowing) {
      setKeyPending(key, true)
      void applyHubAudienceChange(next)
      return
    }
    // Fix round (M2): claim the lock HERE, before the confirm dialog opens —
    // not inside `applyHubAudienceChange`, which only ran once `onOk` fired.
    // The Segmented is disabled (`pending[key]`) from this exact moment, and
    // — just as important — the guard above now blocks a SECOND narrowing
    // click (e.g. the other option, or a repeat click) from ever opening a
    // second confirm dialog while this one is still open. `onCancel`
    // releases the lock again if the admin dismisses without confirming
    // (Cancel, Escape, or the mask), so a cancelled dialog doesn't leave the
    // control disabled forever.
    setKeyPending(key, true)
    if (next === 'off') {
      modal.confirm({
        title: 'Turn off Jaguar-Owned hub?',
        content: `${hubEntry.offImpact} Nothing is deleted, and you can turn it back on at any time.`,
        okText: 'Turn off',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => applyHubAudienceChange('off'),
        onCancel: () => setKeyPending(key, false),
      })
      return
    }
    // The only other narrowing move is Everyone → Admins only.
    modal.confirm({
      title: 'Show Jaguar-Owned to admins only?',
      content:
        'Members stop seeing Jaguar-Owned right away; admins keep the full hub so you can review it. Owners keep their own listing, and nothing is deleted.',
      okText: 'Show admins only',
      cancelText: 'Cancel',
      onOk: () => applyHubAudienceChange('admins'),
      onCancel: () => setKeyPending(key, false),
    })
  }

  const listingsOpen = byKey.get('jaguar_owned_listings_open')?.enabled ?? false
  const partsOnCount = PART_KEYS.filter((k) => byKey.get(k)?.enabled).length

  // No stats while loading (nothing has loaded yet) or on error (nothing
  // real to summarize) — only once a successful load has actually landed.
  const stats: Stat[] =
    isLoading || isError
      ? []
      : [
          {
            label: 'Hub',
            value: hubAudience === 'off' ? 'Off' : hubAudience === 'admins' ? 'Admins only' : 'On',
            icon: Store,
            tone: hubAudience === 'off' ? 'neutral' : hubAudience === 'admins' ? 'gold' : 'green',
          },
          { label: 'Parts on', value: `${partsOnCount} of 5`, icon: Target, tone: 'jaguar' },
          {
            label: 'New listings',
            value: listingsOpen ? 'Open' : 'Closed',
            icon: ListFilter,
            tone: listingsOpen ? 'green' : 'neutral',
          },
        ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Jaguar-Owned',
        description:
          'Turn each part of Jaguar-Owned on or off. Changes reach the app within seconds, and turning a part off hides it without deleting anything.',
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Switches" style={{ maxWidth: 720 }}>
        {isError || retrying ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, marginBottom: 12 }}>
              Couldn&apos;t load the switches. Try again.
            </div>
            <Button data-testid="jo-controls-retry" loading={isFetching} onClick={() => void handleRetry()}>
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13, color: brand.sub, padding: '24px 0' }}>
            Loading…
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {CONTROLS_CATALOG.map((entry, idx) => {
              const row = byKey.get(entry.key) ?? null
              const enabled = row?.enabled ?? false
              const isMaster = entry.kind === 'master'
              const showHint = entry.kind === 'part' && !hubOn

              return (
                <div
                  key={entry.key}
                  data-testid={`jo-control-${entry.key}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    ...(isMaster
                      ? {
                          background: 'rgba(196,151,49,.06)',
                          borderRadius: 10,
                          padding: '18px 14px',
                          marginBottom: 8,
                          border: '1px solid rgba(196,151,49,.22)',
                        }
                      : {
                          padding: '16px 0',
                          borderBottom: idx < CONTROLS_CATALOG.length - 1 ? `1px solid ${brand.cardLine}` : 'none',
                        }),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                      {isMaster ? <Store size={18} color={brand.gold} /> : <Target size={18} color={brand.jaguar} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: isMaster ? 700 : 600,
                          fontSize: isMaster ? 14 : 13,
                          color: brand.ink,
                          marginBottom: 3,
                        }}
                      >
                        {entry.title}
                        {!row && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontFamily: brand.fSans,
                              fontWeight: 700,
                              fontSize: 10,
                              color: brand.sub,
                              background: 'rgba(0,0,0,.05)',
                              padding: '2px 7px',
                              borderRadius: 999,
                              verticalAlign: 'middle',
                            }}
                          >
                            NOT SET
                          </span>
                        )}
                      </div>
                      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }}>
                        {entry.note}
                      </div>
                      {entry.kind === 'independent' ? (
                        <div
                          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11.5, color: brand.jaguar, marginTop: 4 }}
                        >
                          Independent of the hub.
                        </div>
                      ) : null}
                      {showHint ? (
                        <div
                          data-testid={`jo-control-hint-${entry.key}`}
                          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 11.5, color: brand.gold, marginTop: 4 }}
                        >
                          Members won&apos;t see this until the hub is on.
                        </div>
                      ) : null}
                      {row?.updated_at && (
                        <div
                          style={{
                            fontFamily: brand.fSans,
                            fontWeight: 500,
                            fontSize: 11,
                            color: brand.sub,
                            marginTop: 4,
                            opacity: 0.65,
                          }}
                        >
                          Last changed {new Date(row.updated_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    {isMaster ? (
                      <Segmented<HubAudience>
                        data-testid="jo-control-hub-audience"
                        value={hubAudience}
                        disabled={!!pending['jaguar_owned_enabled']}
                        onChange={handleHubAudienceChange}
                        options={HUB_AUDIENCE_OPTIONS}
                      />
                    ) : (
                      <Tooltip title={enabled ? `Turn off "${entry.title}"` : `Turn on "${entry.title}"`}>
                        <Switch
                          data-testid={`jo-control-switch-${entry.key}`}
                          checked={enabled}
                          disabled={!!pending[entry.key]}
                          onChange={(checked) => handleToggle(entry, row, checked)}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>

      {/* Plan deviation 7 (fix round 3): the plan's §2.8/§6 said "shows 'last
          changed' and links to the log" — only "Last changed" shipped. This
          links to the general Audit Trail page unfiltered: `/audit_log`
          (`AuditLogList`, `admin/src/App.tsx:986`) manages its resource/actor
          filters as internal component state (`syncWithLocation: false`,
          `useTable`), never reading them back out of the URL on mount, so
          there is no query-param or Refine-filter contract to link into —
          a pre-filtered URL here would just be ignored by that page. */}
      <div style={{ maxWidth: 720, marginTop: 10 }}>
        <Link
          to="/audit_log"
          data-testid="jo-controls-audit-link"
          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 12.5, color: brand.sub }}
        >
          See who changed these
        </Link>
      </div>
    </ListPage>
  )
}
